import { useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
} from "antd";
import moment from "moment";
import styled from "styled-components";
import { filterTypes, operators } from "./constants";
import { useNewState } from "helpers/utility";

const FilterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px;
  border-top: 1px solid #dee2e6;
`;

const useFilter = (options) => {
  const [isRange, setIsRange] = useState(false);
  const inputRef = useRef();
  let type = filterTypes.TEXT;
  let trueText = "Yes";
  let falseText = "No";
  if (options && options.type) {
    type = options.type;
  }
  if (options && options.trueText) {
    trueText = options.trueText;
  }
  if (options && options.falseText) {
    falseText = options.falseText;
  }
  const operatorList = operators[type];

  const [operator, setOperator] = useState(operatorList[0].value);
  const [listValues, setListValues] = useNewState({
    loading: false,
    textField: options?.data?.textField || "text",
    valueField: options?.data?.valueField || "value",
    data: [],
  });

  useEffect(() => {
    if (options && options.type === filterTypes.LIST && options.data) {
      if (options.data.values) {
        // static list of values
        setListValues({
          data: options.data.values,
        });
      } else {
        // ajax call to get data from server
        setListValues({
          data: [],
        });
      }
    }
    // eslint-disable-next-line
  }, []);

  const handleSearch = (confirm) => {
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  return {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <div>
          <div style={{ padding: "10px" }}>
            <Space direction="vertical">
              <Select
                style={{ width: "100%" }}
                defaultValue={operatorList[0].value}
                value={selectedKeys[0] ? operatorList[0].operator : undefined}
                onChange={(v) => {
                  setOperator(v);
                  setSelectedKeys([
                    {
                      operator: v,
                      value: selectedKeys[0]
                        ? selectedKeys[0].value
                        : undefined,
                    },
                  ]);
                  const hasRange = ["><", "<>"].includes(v);
                  setIsRange(hasRange);
                }}
              >
                {operatorList.map((v) => (
                  <Select.Option key={v.value} value={v.value}>
                    {v.text}
                  </Select.Option>
                ))}
              </Select>
              {type === filterTypes.TEXT && (
                <Input
                  ref={inputRef}
                  // placeholder={`Filter ${dataIndex}`}
                  value={selectedKeys[0] ? selectedKeys[0].value : undefined}
                  onChange={(e) => {
                    setSelectedKeys(
                      e.target.value
                        ? [
                            {
                              operator,
                              value: e.target.value,
                            },
                          ]
                        : []
                    );
                  }}
                />
              )}
              {type === filterTypes.NUMBER && !isRange && (
                <InputNumber
                  ref={inputRef}
                  style={{ width: "100%" }}
                  precision={0}
                  value={selectedKeys[0] ? selectedKeys[0].value : undefined}
                  onChange={(val) => {
                    setSelectedKeys(
                      val
                        ? [
                            {
                              operator,
                              value: val,
                            },
                          ]
                        : []
                    );
                  }}
                />
              )}
              {type === filterTypes.NUMBER && isRange && (
                <Space direction="vertical">
                  <InputNumber
                    ref={inputRef}
                    style={{ width: "100%" }}
                    precision={0}
                    value={selectedKeys[0] ? selectedKeys[0].value : undefined}
                    onChange={(val) => {
                      setSelectedKeys(
                        val
                          ? [
                              {
                                operator,
                                value: val,
                              },
                              selectedKeys[1],
                            ]
                          : []
                      );
                    }}
                  />
                  <InputNumber
                    ref={inputRef}
                    style={{ width: "100%" }}
                    precision={0}
                    value={selectedKeys[1] ? selectedKeys[1].value : undefined}
                    onChange={(val) => {
                      setSelectedKeys(
                        val
                          ? [
                              selectedKeys[0],
                              {
                                operator,
                                value: val,
                              },
                            ]
                          : []
                      );
                    }}
                  />
                </Space>
              )}

              {type === filterTypes.FLOAT && !isRange && (
                <InputNumber
                  ref={inputRef}
                  style={{ width: "100%" }}
                  value={selectedKeys[0] ? selectedKeys[0].value : undefined}
                  onChange={(val) => {
                    setSelectedKeys(
                      val
                        ? [
                            {
                              operator,
                              value: val,
                            },
                          ]
                        : []
                    );
                  }}
                />
              )}
              {type === filterTypes.FLOAT && isRange && (
                <Space direction="vertical">
                  <InputNumber
                    ref={inputRef}
                    style={{ width: "100%" }}
                    value={selectedKeys[0] ? selectedKeys[0].value : undefined}
                    onChange={(val) => {
                      setSelectedKeys(
                        val
                          ? [
                              {
                                operator,
                                value: val,
                              },
                              selectedKeys[1],
                            ]
                          : []
                      );
                    }}
                  />
                  <InputNumber
                    ref={inputRef}
                    style={{ width: "100%" }}
                    value={selectedKeys[1] ? selectedKeys[1].value : undefined}
                    onChange={(val) => {
                      setSelectedKeys(
                        val
                          ? [
                              selectedKeys[0],
                              {
                                operator,
                                value: val,
                              },
                            ]
                          : []
                      );
                    }}
                  />
                </Space>
              )}

              {type === filterTypes.DATE && !isRange && (
                <DatePicker
                  ref={inputRef}
                  value={
                    selectedKeys[0] && selectedKeys[0].value
                      ? moment(selectedKeys[0].value)
                      : undefined
                  }
                  onChange={(val, dtString) => {
                    setSelectedKeys(
                      val
                        ? [
                            {
                              operator,
                              value: dtString,
                            },
                          ]
                        : []
                    );
                  }}
                />
              )}

              {type === filterTypes.DATE && isRange && (
                <Space direction="vertical">
                  <DatePicker
                    ref={inputRef}
                    placeholder="From date"
                    value={
                      selectedKeys[0] && selectedKeys[0].value
                        ? moment(selectedKeys[0].value)
                        : undefined
                    }
                    onChange={(val, dtString) => {
                      setSelectedKeys(
                        val
                          ? [
                              {
                                operator,
                                value: dtString,
                              },
                              selectedKeys[1],
                            ]
                          : []
                      );
                    }}
                  />
                  <DatePicker
                    ref={inputRef}
                    placeholder="To date"
                    value={
                      selectedKeys[1] && selectedKeys[1].value
                        ? moment(selectedKeys[1].value)
                        : undefined
                    }
                    onChange={(val, dtString) => {
                      setSelectedKeys(
                        val
                          ? [
                              selectedKeys[0],
                              {
                                operator,
                                value: dtString,
                              },
                            ]
                          : []
                      );
                    }}
                  />
                </Space>
              )}

              {type === filterTypes.YESNO && (
                <Radio.Group
                  buttonStyle="solid"
                  defaultValue="1"
                  ref={inputRef}
                  style={{ width: "140px" }}
                  value={selectedKeys[0] ? selectedKeys[0].value : undefined}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedKeys(
                      val
                        ? [
                            {
                              operator,
                              value: val,
                            },
                          ]
                        : []
                    );
                  }}
                >
                  <Radio.Button value="1">{trueText}</Radio.Button>
                  <Radio.Button value="0">{falseText}</Radio.Button>
                </Radio.Group>
              )}
              {type === filterTypes.LIST && (
                <Select
                  style={{ width: "180px" }}
                  ref={inputRef}
                  loading={listValues.loading}
                  value={selectedKeys[0] ? selectedKeys[0].value : undefined}
                  onChange={(val) => {
                    setSelectedKeys(
                      val
                        ? [
                            {
                              operator,
                              value: val,
                            },
                          ]
                        : []
                    );
                  }}
                >
                  {listValues.data.map((v) => (
                    <Select.Option
                      key={v[listValues.valueField]}
                      value={v[listValues.valueField]}
                    >
                      {v[listValues.textField]}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Space>
          </div>
          <FilterButtons>
            <Button type="primary" onClick={() => handleSearch(confirm)}>
              Filter
            </Button>
            <Button onClick={() => handleReset(clearFilters, confirm)}>
              Clear
            </Button>
          </FilterButtons>
        </div>
      );
    },
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => {
          if (inputRef.current) {
            if (inputRef.current.select) {
              inputRef.current.select();
            } else {
              inputRef.current.focus();
            }
          }
        }, 10);
      }
    },
  };
};
export default useFilter;
